import axios from "axios";
import { PLATFORM_CONFIG } from "../config/ApiConfig";
import { nutritionBody, nutritionHeaders, platformRequest, platformUser, usesPhpNutrition } from "./PlatformAuth";

/**
 * Customer shop on the new platform: product screens come from the
 * nutrition service's storefront (listings + nutrition in one call), the
 * catalog and cart from the commerce service.
 */

const query = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  ).toString();
  return qs ? `?${qs}` : "";
};

/** Public GET; errors carry `status` so screens can tell "not found" from "offline". */
async function publicGet(url, signal) {
  const isNutrition = url.startsWith(PLATFORM_CONFIG.NUTRITION_API);
  try {
    const { data } = await axios.get(url, { signal, headers: isNutrition ? nutritionHeaders() : {} });
    return isNutrition ? nutritionBody(data) : data;
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    const failure = new Error(
      error.response
        ? [error.response.data?.message].flat().join(", ") || "Request failed"
        : "Could not reach the server. Check your connection.",
    );
    failure.status = error.response?.status ?? 0;
    throw failure;
  }
}

const storefront = (path) => `${PLATFORM_CONFIG.NUTRITION_API}nutrition/storefront/${path}`;

/**
 * Product cards for a store.
 * params: { subCategoryId?, categoryId?, search?, filter?, sort?, page?, limit? }
 * -> { subCategory, filters: [{code, count}], items, page, total, totalPages }
 */
export const getShopProducts = (storeId, params, signal) =>
  publicGet(storefront(`stores/${storeId}/products${query(params)}`), signal);

/** Product + packs + nutrition (null when unavailable) + delivery + recipe preview. */
export const getShopProduct = (storeId, productId, location, signal) =>
  publicGet(storefront(`stores/${storeId}/products/${productId}${query(location)}`), signal);

export const getProductRecipes = (productId, limit = 50) =>
  publicGet(`${PLATFORM_CONFIG.NUTRITION_API}nutrition/products/${productId}/recipes${query({ limit })}`);

export const getRecipe = (id) => publicGet(`${PLATFORM_CONFIG.NUTRITION_API}nutrition/recipes/${id}`);

/** Categories with their sub-categories. */
export const getShopCategories = () => publicGet(`${PLATFORM_CONFIG.COMMERCE_API}categories`);

/** Stores for the store picker. */
export const getShopStores = (params = {}) =>
  publicGet(`${PLATFORM_CONFIG.COMMERCE_API}stores${query({ limit: 100, ...params })}`);

/**
 * Product master (public) — the editor reads localName, attributes... from here.
 * The PHP API keeps those shop details itself, behind the admin sign-in.
 */
export const getPlatformProduct = (productId) =>
  usesPhpNutrition()
    ? nutrition("get", `admin/products/${productId}/shop-details`)
    : publicGet(`${PLATFORM_CONFIG.COMMERCE_API}products/${productId}`);

// ---- signed-in calls ----

const commerce = (method, path, data) => platformRequest(PLATFORM_CONFIG.COMMERCE_API, method, path, data);
const nutrition = (method, path, data) => platformRequest(PLATFORM_CONFIG.NUTRITION_API, method, `nutrition/${path}`, data);

export const isSignedInToPlatform = () => platformUser() !== null;

export const getCart = (storeId) => commerce("get", `cart${query({ storeId })}`);
export const addCartItem = (priceId, quantity = 1, replaceCart = false) =>
  commerce("post", "cart/items", { priceId, quantity, ...(replaceCart && { replaceCart }) });
export const updateCartItem = (itemId, quantity) => commerce("patch", `cart/items/${itemId}`, { quantity });

/** Shop fields of the product master: { localName, qualityTags, attributes, badge }. */
export const updateProductShopDetails = (productId, body) =>
  usesPhpNutrition()
    ? nutrition("put", `admin/products/${productId}/shop-details`, body)
    : commerce("patch", `products/${productId}`, body);

// ---- recipes (admin) ----

export const listRecipes = (params) => nutrition("get", `admin/recipes${query(params)}`);
export const createRecipe = (body) => nutrition("post", "admin/recipes", body);
export const updateRecipe = (id, body) => nutrition("patch", `admin/recipes/${id}`, body);
export const deleteRecipe = (id) => nutrition("delete", `admin/recipes/${id}`);

/** Product search for linking recipes: { items: [{ id, name }] }. */
export const searchPlatformProducts = (search) =>
  usesPhpNutrition()
    ? nutrition("get", `admin/combo-products${query({ search, limit: 10 })}`)
    : publicGet(`${PLATFORM_CONFIG.COMMERCE_API}products${query({ search, limit: 10 })}`);
